import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Instagram, Mail, Send } from "lucide-react";
import { Reveal, ChapterHeading } from "./Reveal";
import { CONTACT, CONTACT_SECTION, IMAGES, waLink } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-gold/25 focus:border-gold py-3 text-cream placeholder:text-mist/50 outline-none transition-colors duration-300 text-base";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Mesajınız ulaştı. En kısa sürede size dönüş yapacağım.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen WhatsApp üzerinden ulaşmayı deneyin.");
    } finally {
      setSending(false);
    }
  };

  const channels = [
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: CONTACT.whatsappDisplay,
      href: waLink("Merhaba, bilgi almak istiyorum."),
      testId: "contact-channel-whatsapp",
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: CONTACT.instagram,
      href: CONTACT.instagramUrl,
      testId: "contact-channel-instagram",
    },
    {
      icon: Mail,
      label: "E-posta",
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      testId: "contact-channel-email",
    },
  ];

  return (
    <section id="iletisim" data-testid="contact-section" className="relative z-10 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <ChapterHeading
            chapter={CONTACT_SECTION.chapter}
            label={CONTACT_SECTION.label}
            title={CONTACT_SECTION.title}
            subtext={CONTACT_SECTION.subtext}
          />
          <Reveal delay={0.1}>
            <div className="mt-9 max-w-[240px] rounded-2xl border border-gold/25 bg-navy-800/40 p-2">
              <img
                src={IMAGES.contactPortrait}
                alt="Astrolog portresi"
                data-testid="contact-portrait"
                className="aspect-[4/5] w-full rounded-xl object-cover"
                style={{ objectPosition: "center 20%" }}
              />
            </div>
          </Reveal>
          <div className="mt-10 space-y-4">
            {channels.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.1}>
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={c.testId}
                  className="group flex items-center gap-5 rounded-2xl border border-gold/20 bg-navy-800/60 px-6 py-5 transition-all duration-300 hover:border-gold/60 hover:bg-navy-800"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
                    <c.icon className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-mist">
                      {c.label}
                    </span>
                    <span className="block mt-1 text-cream">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-gold/20 bg-navy-800/60 backdrop-blur-sm p-7 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-1">
                  Adınız Soyadınız *
                </label>
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Örn: Ayşe Yılmaz"
                  data-testid="contact-input-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-1">
                  E-posta *
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="ornek@email.com"
                  data-testid="contact-input-email"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-1">
                  Telefon (WhatsApp)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="0532 ..."
                  data-testid="contact-input-phone"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-1">
                  İlgilendiğiniz Konu
                </label>
                <select
                  value={form.service}
                  onChange={set("service")}
                  data-testid="contact-select-service"
                  className={`${inputCls} bg-navy-800/0 [&>option]:bg-navy-800`}
                >
                  <option value="">Seçiniz</option>
                  {CONTACT_SECTION.serviceOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-1">
                  Mesajınız *
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Sorunuz veya iletmek istediğiniz detaylar..."
                  data-testid="contact-input-message"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-9 inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-gold px-9 py-4 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] disabled:opacity-60"
            >
              {sending ? "Gönderiliyor..." : "Mesaj Gönder"}
              <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
